import { useEffect, useMemo, useState } from "react";
import { NotificationTypes } from "@repo/database";
import { format, formatDistance } from "date-fns";
import { Bell, Calendar } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";

import type { Notification } from "~/providers/notification-provider";
import { useMediaQuery } from "~/hooks/use-media-query";
import { api } from "~/lib/api";
import { useBookingManager } from "~/managers/use-booking-manager";
import { useNotificationManager } from "~/managers/use-notification-manager";
import { Button } from "../ui/button";
import { Drawer, DrawerContent, DrawerTrigger } from "../ui/drawer";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

function NotificationItem({
  notification,
  onClose,
}: {
  notification: Notification;
  onClose: () => void;
}) {
  const { openBookingById } = useBookingManager();
  const { t } = useTranslation("common");

  return (
    <li className="border-t first:border-t-0 flex">
      <button
        onClick={() => {
          if (notification.booking) {
            void openBookingById(notification.booking.id);
            onClose();
          }
        }}
        className="rounded-none text-start w-full flex items-start justify-start h-auto gap-6 overflow-hidden p-2 transition-colors hover:bg-muted"
      >
        {notification.type === NotificationTypes.new_booking && (
          <div className="rounded-full w-8 h-8 bg-gray-700 p-2 flex items-center justify-center">
            <Calendar />
          </div>
        )}
        {notification.type === NotificationTypes.canceled_booking && (
          <div className="rounded-full w-8 h-8 bg-orange-700 p-2 flex items-center justify-center">
            <Calendar />
          </div>
        )}

        <div className="flex flex-col">
          <div className="flex">
            {notification.type === NotificationTypes.new_booking &&
              notification.booking && (
                <p className="text-sm">
                  {t("Notifications.new_booking", {
                    start_date: format(notification.booking.startTime, "P"),
                    start_time: format(notification.booking.startTime, "p"),
                    group_size: notification.booking.groupSize,
                  })}
                </p>
              )}
            {notification.type === NotificationTypes.updated_booking &&
              notification.booking && (
                <p className="text-sm">
                  {t("Notifications.updated_booking", {
                    start_date: format(notification.booking.startTime, "P"),
                    start_time: format(notification.booking.startTime, "p"),
                    group_size: notification.booking.groupSize,
                  })}
                </p>
              )}
            {notification.type === NotificationTypes.canceled_booking &&
              notification.booking && (
                <p className="text-sm">
                  {t("Notifications.canceled_booking", {
                    start_date: format(notification.booking.startTime, "P"),
                    start_time: format(notification.booking.startTime, "p"),
                    group_size: notification.booking.groupSize,
                  })}
                </p>
              )}
          </div>
          <div className="flex">
            <p className="text-muted-foreground text-sm">
              {formatDistance(notification.createdAt, new Date(), {
                addSuffix: true,
              })}
            </p>
          </div>
        </div>
      </button>
    </li>
  );
}

function NotificationList({
  onArchiveAll,
  onClose,
  notifications,
  scrollRef,
}: {
  notifications: Notification[];
  onClose: () => void;
  onArchiveAll: () => void;
  scrollRef: React.RefObject<HTMLElement>;
}) {
  const { t } = useTranslation("common");

  return (
    <>
      <div className="border-b text-center p-2">
        {t("Notifications.notification", { count: 0 })}
      </div>

      {notifications.length === 0 && (
        <div className="flex flex-col items-center justify-center flex-1">
          {t("Notifications.no_notifications")}
        </div>
      )}

      {notifications.length > 0 && (
        <div className="flex flex-col overflow-auto flex-grow">
          <ul className="flex flex-col overflow-auto small-scrollbar border-b flex-grow">
            {notifications
              .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
              .map((notification) => (
                <NotificationItem
                  notification={notification}
                  onClose={onClose}
                  key={notification.id}
                />
              ))}
            <li ref={scrollRef} className="flex justify-center p-2">
              {/* Add your loading spinner component here */}
              {/* {isFetchingNextPage && <Spinner className="h-5 w-5" />} */}
            </li>
          </ul>
          <Button
            className="rounded-none"
            onClick={onArchiveAll}
            variant="ghost"
          >
            {t("Notifications.archive_all")}
          </Button>
        </div>
      )}
    </>
  );
}

export default function Notifications() {
  const {
    data: notificationPages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = api.notification.getPaginated.useInfiniteQuery(
    { offset: 0, limit: 15 },
    {
      getNextPageParam: (lastPage) => {
        // Check if lastPage and notifications exist
        if (!lastPage || !lastPage.notifications) return undefined;
        
        // Stop fetching if last page had fewer notifications than limit
        if (lastPage.notifications.length < 15) return undefined;

        // Return next offset (current offset + limit)
        return lastPage.notifications.length;
      },
      initialCursor: 0,
    },
  );

  const { mutate: markAsSeen } = api.notification.markAsSeen.useMutation({
    async onMutate(ids) {
      // 1. Cancel any ongoing infinite queries
      await utils.notification.getPaginated.cancel();

      // 2. Get previous infinite query data
      const prevData = utils.notification.getPaginated.getInfiniteData({
        limit: 15,
        offset: 0,
      });

      // 3. Optimistically update infinite pages
      utils.notification.getPaginated.setInfiniteData(
        { limit: 15, offset: 0 },
        (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              notifications: page.notifications.map((notification) =>
                ids.includes(notification.id)
                  ? { ...notification, seenAt: new Date() }
                  : notification,
              ),
            })),
          };
        },
      );

      return { prevData };
    },
    onError(_err, _ids, context) {
      // 4. Rollback to previous data on error
      utils.notification.getPaginated.setInfiniteData(
        { limit: 15, offset: 0 },
        context?.prevData,
      );
    },
    onSettled() {
      // 5. Invalidate to refresh from server
      utils.notification.getPaginated.invalidate();
    },
  });

  const { ref, inView } = useInView({ threshold: 0 });

  const notifications = useMemo(
    () => notificationPages?.pages.flatMap((p) => p.notifications) ?? [],
    [notificationPages],
  );
  const [notificationsExpanded, setNotificationsExpanded] = useState(false);

  const utils = api.useUtils();

  const isMobile = useMediaQuery(768);

  const { archiveAll } = useNotificationManager();

  const unArchivedNotifications = notifications.filter(
    (notification) => !notification.archivedAt,
  );

  const unSeenNotifications = unArchivedNotifications.filter(
    (notification) => !notification.seenAt,
  );

  const handleArchiveAll = () => {
    void archiveAll();
    setNotificationsExpanded(false);
  };

  const unseenIds = useMemo(
    () => unSeenNotifications.map((n) => n.id),
    [unSeenNotifications], // Only recompute when actual notifications change
  );

  const handleOpenChange = (isOpen: boolean) => {
    setNotificationsExpanded(isOpen);

    // Mark as seen when opening
    if (isOpen && unseenIds.length > 0) {
      console.log("MARK AS SEEN");
      markAsSeen(unseenIds);
    }
  };

  useEffect(() => {
    console.log(`is in view: ${inView}`);
    console.log(`hasNextPage: ${hasNextPage}`);
    if (!inView || !notificationsExpanded) return;
    if (!isFetchingNextPage && hasNextPage) {
      void fetchNextPage();
    }
  }, [
    inView,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    notificationsExpanded,
  ]);

  if (isMobile)
    return (
      <Drawer open={notificationsExpanded} onOpenChange={handleOpenChange}>
        <DrawerTrigger asChild>
          <Button variant="ghost" className="relative">
            {unSeenNotifications.length > 0 && (
              <div className="p-1 absolute text-xs w-6 h-6 flex items-center justify-center -top-1 -right-0 rounded-full bg-red-500 text-white">
                {unSeenNotifications.length}
              </div>
            )}
            <Bell />
          </Button>
        </DrawerTrigger>
        <DrawerContent className="max-h-[80dvh] min-h-[40dvh] overflow-hidden flex flex-col p-0">
          <NotificationList
            scrollRef={ref as React.RefObject<HTMLElement>}
            onClose={() => handleOpenChange(false)}
            onArchiveAll={handleArchiveAll}
            notifications={unArchivedNotifications}
          />
        </DrawerContent>
      </Drawer>
    );

  return (
    <Popover
      open={notificationsExpanded}
      onOpenChange={setNotificationsExpanded}
    >
      <PopoverTrigger asChild>
        <Button variant="ghost" className="relative">
          {unSeenNotifications.length > 0 && (
            <div className="p-1 absolute text-xs w-6 h-6 flex items-center justify-center -top-1 -right-0 rounded-full bg-red-500 text-white">
              {unSeenNotifications.length}
            </div>
          )}
          <Bell />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="max-h-[80dvh] min-h-[40dvh] w-[350px] overflow-hidden flex flex-col p-0"
        align="end"
      >
        <NotificationList
          scrollRef={ref as React.RefObject<HTMLElement>}
          onClose={() => setNotificationsExpanded(false)}
          onArchiveAll={handleArchiveAll}
          notifications={unArchivedNotifications}
        />
      </PopoverContent>
    </Popover>
  );
}
